import { angkasaRound } from "@jomcerdik/shared";
import { api } from "../../../../lib/api";
import { shuffle } from "../common";
import type { ArcadeQ } from "./types";
import { bahasaRound } from "./bahasa";

export type Topic = "mat" | "sains" | "campur" | "bm" | "bi" | "semua";

interface Subj { versionId: string; subject: string }
interface DeckRow { stemMs: string; stemEn: string; optionsMs: string[]; optionsEn: string[]; answer: number }

/** Maths: generated, symbols only (reads the same in every language). */
function maths(year: number, n: number): ArcadeQ[] {
  return angkasaRound(year, n).map((q) => ({ text: q.text, options: q.options, answer: Math.max(0, q.options.indexOf(q.answer)) }));
}

/** Sains (or any subject with a question bank): short questions with four short options, so they fit on the boards. */
async function sains(year: number, lang: "ms" | "en", n: number, subject = "sains", match = /sains|science/i): Promise<ArcadeQ[]> {
  const y = await api<{ subjects: Subj[] }>(`/api/learn/year/rendah/${year}`);
  const sub = y.subjects.find((s) => s.subject === subject) ?? y.subjects.find((s) => match.test(s.subject));
  if (!sub) return [];
  const d = await api<{ questions: DeckRow[] }>(`/api/learn/games/deck?versionId=${sub.versionId}`);
  const all: ArcadeQ[] = [];
  for (const r of d.questions) {
    const en = lang === "en" && r.optionsEn?.length === 4;
    const options = (en ? r.optionsEn : r.optionsMs?.length === 4 ? r.optionsMs : r.optionsEn)?.map((o) => String(o).trim()) ?? [];
    const text = ((lang === "en" ? r.stemEn || r.stemMs : r.stemMs || r.stemEn) ?? "").trim();
    if (options.length !== 4 || !text || new Set(options).size !== 4) continue;
    if (r.answer < 0 || r.answer > 3) continue;
    all.push({ text, options, answer: r.answer, lang: en ? "en" : "ms" });
  }
  const fit = (maxStem: number, maxOpt: number) => all.filter((q) => q.text.length <= maxStem && q.options.every((o) => o.length <= maxOpt));
  let pool = fit(90, 20);
  if (pool.length < n) pool = fit(150, 28);
  if (pool.length < n) pool = all;
  return shuffle(pool).slice(0, n);
}

/** A round of `n` questions for the chosen topic and year. Falls back to maths if the question bank has nothing. */
export async function loadRound(topic: Topic, year: number, lang: "ms" | "en", n = 10): Promise<ArcadeQ[]> {
  if (topic === "mat") return maths(year, n);
  if (topic === "bm" || topic === "bi") return bahasa(topic, year, n);
  if (topic === "semua") return semua(year, lang, n);
  let sc: ArcadeQ[] = [];
  try { sc = await sains(year, lang, topic === "campur" ? Math.ceil(n / 2) : n); } catch { sc = []; }
  if (topic === "sains") return sc.length >= 4 ? sc : [...sc, ...maths(year, n - sc.length)];
  return shuffle([...sc, ...maths(year, n - sc.length)]);
}

/** BM / BI: a few short questions from the subject's own question bank, the rest generated word questions (spelling, opposites…).
   The language is the subject's, not the UI's: a BM round is in Bahasa Melayu, a BI round in English. */
async function bahasa(topic: "bm" | "bi", year: number, n: number): Promise<ArcadeQ[]> {
  let bank: ArcadeQ[] = [];
  try {
    bank = topic === "bm"
      ? await sains(year, "ms", n, "bahasa_melayu", /bahasa.?melayu/i)
      : await sains(year, "en", n, "bahasa_inggeris", /inggeris|english/i);
  } catch { bank = []; }
  bank = bank.filter((q) => q.text.length <= 70 && q.options.every((o) => o.length <= 18)).map((q) => ({ ...q, lang: topic === "bm" ? "ms" as const : "en" as const })).slice(0, Math.floor(n * 0.3));
  return shuffle([...bank, ...bahasaRound(topic, year, n - bank.length)]);
}

/** Every subject in one round: Matematik, Sains, Bahasa Melayu, English and (Tahun 4–6) Sejarah, a couple of questions each.
   Maths, Sains and Sejarah follow the UI language; BM and BI questions stay in their own language. */
async function semua(year: number, lang: "ms" | "en", n: number): Promise<ArcadeQ[]> {
  const safe = async (f: () => Promise<ArcadeQ[]>) => { try { return await f(); } catch { return []; } };
  const [sc, sj] = await Promise.all([
    safe(() => sains(year, lang, 2)),
    year >= 4 ? safe(() => sains(year, lang, 2, "sejarah", /sejarah|history/i)) : Promise.resolve([] as ArcadeQ[]),
  ]);
  const bm = bahasaRound("bm", year, 2), bi = bahasaRound("bi", year, 2);
  const mat = maths(year, Math.max(2, n - sc.length - sj.length - bm.length - bi.length)).map((q) => ({ ...q, lang }));
  return shuffle([...mat, ...sc, ...sj, ...bm, ...bi]).slice(0, n);
}
